import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@vtak/shared';
import { fetchJson } from '@/services/api-client';
import type { UpcomingLaunch, LaunchStats, LaunchLocation } from '@vtak/mod-space/client';

/** Launch-dashboard data, cached + polled (every 60s) only while the dashboard is open. */
const POLL_MS = 60_000;
const STALE_MS = 30_000;

/**
 * GET /launches URL, scoped with one repeated `sites` param per site name.
 * Names are LL2 catalog names that themselves contain commas ("Cape Canaveral
 * SFS, FL, USA"), so they are never comma-joined into a single value.
 */
function launchesUrl(sites: string[]): string {
  if (sites.length === 0) return '/api/space/launches';
  const params = new URLSearchParams();
  for (const site of sites) params.append('sites', site);
  return `/api/space/launches?${params.toString()}`;
}

/**
 * Upcoming launches (polls while `enabled`). Pass `sites` to scope the pool
 * server-side to those launch sites (every upcoming launch there, not the
 * next few worldwide); empty/omitted serves the global schedule.
 */
export function useUpcomingLaunches(enabled: boolean, sites: string[] = []): UseQueryResult<UpcomingLaunch[]> {
  const sorted = [...sites].sort((a, b) => a.localeCompare(b));
  return useQuery({
    queryKey: sorted.length > 0 ? queryKeys.space.launchesForSites(sorted) : queryKeys.space.launches,
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: () => fetchJson<UpcomingLaunch[]>(launchesUrl(sorted), []),
  });
}

/** Static LL2 launch-location catalog (focus-editor site options; effectively static, cached 1 h). */
export function useLaunchLocations(): UseQueryResult<LaunchLocation[]> {
  return useQuery({
    queryKey: queryKeys.space.launchLocations,
    staleTime: 3_600_000,
    queryFn: () => fetchJson<LaunchLocation[]>('/api/space/launch-locations', []),
  });
}

/** Recent previous launches (polls while `enabled`). */
export function usePreviousLaunches(enabled: boolean): UseQueryResult<UpcomingLaunch[]> {
  return useQuery({
    queryKey: queryKeys.space.launchesPrevious,
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: () => fetchJson<UpcomingLaunch[]>('/api/space/launches/previous', []),
  });
}

/** Launch statistics (polls while `enabled`). */
export function useLaunchStats(enabled: boolean): UseQueryResult<LaunchStats> {
  return useQuery({
    queryKey: queryKeys.space.launchStats,
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: () => fetchJson<LaunchStats>('/api/space/launches/stats', { totalThisYear: 0, successesThisYear: 0 }),
  });
}
